import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    FiHome,
    FiMail,
    FiUser,
    FiBriefcase,
    FiMapPin,
    FiCheckCircle,
    FiAlertCircle,
    FiClock,
    FiArrowLeft
} from "react-icons/fi";
import API_BASE_URL from "../config";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import "../styles/UserVerification.css";

export default function UserVerification() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(2);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("error");
    const [logoUrl, setLogoUrl] = useState(null);
    const GEOAPIFY_API_KEY = "bf58eac2ef4d43d3b7c5661f657f2a03";
    const [citySuggestions, setCitySuggestions] = useState([]);


    /* ==============================
       LOAD LOGO FROM SITE SETTINGS
    ============================== */
    useEffect(() => {
        const fetchLogo = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/site-settings`);
                const data = await res.json();

                if (data?.logo && data?.logoContentType) {
                    const base64 = `data:${data.logoContentType};base64,${data.logo}`;
                    setLogoUrl(base64);
                }
            } catch (err) {
                console.error("Failed to load logo:", err);
            }
        };

        fetchLogo();
    }, []);

    /* ==============================
       FORM STATE - FIXED INITIALIZATION
    ============================== */
    const [form, setForm] = useState(() => {
        const locationData = location.state?.form || {};

        // Ensure otp is always an array with 6 empty strings
        return {
            name: locationData.name || "",
            email: locationData.email || "",
            countryCode: locationData.countryCode || "+44",
            mobile: locationData.mobile || "",
            password: locationData.password || "",
            otp: locationData.otp || ["", "", "", "", "", ""], // Always initialize as array
            role: locationData.role || "",
            sraNumber: locationData.sraNumber || "",
            city: locationData.city || ""
        };
    });

    /* ==============================
       OTP INPUT REFS
    ============================== */
    const otpRefs = useRef(Array(6).fill(null));

    const fetchCities = async (value) => {

        setForm(prev => ({ ...prev, city: value }));

        if (value.length < 2) {
            setCitySuggestions([]);
            return;
        }



        try {
            const res = await fetch(
                `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
                    value
                )}&limit=6&apiKey=${GEOAPIFY_API_KEY}`
            );

            const data = await res.json();

            const locations =
                data.features?.map((item) => ({
                    id: item.properties.place_id,
                    city:
                        item.properties.city ||
                        item.properties.town ||
                        item.properties.name ||
                        "",
                    country: item.properties.country || "",
                    label: [
                        item.properties.city ||
                        item.properties.town ||
                        item.properties.name,
                        item.properties.country
                    ]
                        .filter(Boolean)
                        .join(", ")
                })) || [];

            setCitySuggestions(locations);

        } catch (err) {
            console.error("Geoapify city autocomplete error", err);
        } finally {

        }
    };


    /* ==============================
       OTP TIMER
    ============================== */
    const OTP_EXPIRY_SECONDS = 300;
    const [otpTimer, setOtpTimer] = useState(OTP_EXPIRY_SECONDS);
    const [canResend, setCanResend] = useState(false);

    useEffect(() => {
        if (!form.email) {
            navigate("/signup");
        }
    }, [form.email, navigate]);

    useEffect(() => {
        if (otpTimer <= 0) {
            setCanResend(true);
            return;
        }

        const interval = setInterval(() => {
            setOtpTimer((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [otpTimer]);

    const formatTime = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    };

    /* ==============================
       OTP INPUT HANDLING - FIXED
    ============================== */
    const handleOtpChange = (index, value) => {
        // Ensure otp array exists
        const currentOtp = form.otp || ["", "", "", "", "", ""];

        // Allow only digits
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...currentOtp];
        newOtp[index] = value.slice(-1); // Take only last character

        setForm({ ...form, otp: newOtp });

        // Auto-focus next input if value entered
        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }

        // If deleting, focus previous input
        if (!value && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        // Ensure otp array exists
        const currentOtp = form.otp || ["", "", "", "", "", ""];

        // Handle backspace
        if (e.key === 'Backspace' && !currentOtp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }

        // Handle paste
        if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            navigator.clipboard.readText().then(text => {
                const digits = text.replace(/\D/g, '').slice(0, 6);
                const newOtp = [...currentOtp];

                digits.split('').forEach((digit, idx) => {
                    if (idx < 6) {
                        newOtp[idx] = digit;
                    }
                });

                setForm({ ...form, otp: newOtp });

                // Focus last filled input or first empty
                const lastFilledIndex = digits.length - 1;
                if (lastFilledIndex < 5) {
                    otpRefs.current[lastFilledIndex + 1]?.focus();
                } else {
                    otpRefs.current[5]?.focus();
                }
            });
        }
    };

    const getOtpString = () => {
        const currentOtp = form.otp || ["", "", "", "", "", ""];
        return currentOtp.join('');
    };

    /* ==============================
       VERIFY OTP (STEP 2)
    ============================== */
    const verifyOtp = async () => {
        const otpString = getOtpString();

        if (otpString.length !== 6) {
            setMessage("Please enter the complete 6-digit OTP");
            setMessageType("error");
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: form.email,
                    otp: otpString
                })
            });

            const data = await res.json();

            if (res.ok) {
                setStep(3);
                setOtpTimer(0);
                setMessage("OTP verified successfully!");
                setMessageType("success");
            } else {
                setMessage(data.message || "Invalid OTP");
                setMessageType("error");

                // Clear OTP on error
                setForm({ ...form, otp: ["", "", "", "", "", ""] });
                otpRefs.current[0]?.focus();
            }
        } catch (err) {
            console.error(err);
            setMessage("Something went wrong. Please try again.");
            setMessageType("error");
        } finally {
            setLoading(false);
        }
    };

    /* ==============================
       RESEND OTP
    ============================== */
    const resendOtp = async () => {
        if (!canResend) return;

        setLoading(true);
        setMessage("");

        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/resend-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: form.email
                })
            });

            const data = await res.json();

            if (res.ok) {
                setOtpTimer(OTP_EXPIRY_SECONDS);
                setCanResend(false);
                setMessage("OTP resent successfully! Check your email.");
                setMessageType("success");

                // Clear OTP inputs
                setForm({ ...form, otp: ["", "", "", "", "", ""] });
                otpRefs.current[0]?.focus();
            } else {
                setMessage(data.message || "Failed to resend OTP");
                setMessageType("error");
            }
        } catch (err) {
            console.error(err);
            setMessage("Failed to resend OTP. Please try again.");
            setMessageType("error");
        } finally {
            setLoading(false);
        }
    };


    /* ==============================
   CREATE ACCOUNT (STEP 3)
   → Redirect to Subscription Checkout
============================== */
    const createAccount = async () => {
        if (!form.role) {
            setMessage("Please select your role");
            setMessageType("error");
            return;
        }

        if (form.role === "SOLICITOR" && !form.sraNumber) {
            setMessage("Please enter your SRA Number");
            setMessageType("error");
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            const signupData = {
                name: form.name,
                email: form.email,
                mobile: form.countryCode + form.mobile,
                password: form.password,
                role: form.role,
                sraNumber: form.role === "SOLICITOR" ? form.sraNumber : null,
                city: form.role === "SOLICITOR" ? form.city : null
            };

            /* ==============================
               ✅ IF USER → CREATE DIRECTLY
            ============================== */
            if (form.role === "USER") {

                const res = await fetch(`${API_BASE_URL}/api/auth/register`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(signupData)
                });

                const data = await res.json();

                if (!res.ok) {
                    toast.error(data.message || "Failed to create account");
                    return;
                }

                login(data.token, data.user);

                toast.success("Account created successfully");

                navigate("/user/dashboard", { replace: true });
                return;
            }

            /* ==============================
               🔥 IF SOLICITOR → VERIFY SRA FIRST
            ============================== */
            if (form.role === "SOLICITOR") {

                const verifyRes = await fetch(`${API_BASE_URL}/api/auth/verify-sra`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        sraNumber: form.sraNumber
                    })
                });

                const verifyData = await verifyRes.json();

                if (!verifyRes.ok) {
                    toast.error(verifyData.message || "SRA verification failed");
                    return;
                }

                // ✅ SUCCESS TOAST
                toast.success("SRA Verified Successfully");

                // 👉 Now move to subscription
                navigate("/subscription/checkout", {
                    state: { signupData }
                });
            }

        } catch (err) {
            console.error(err);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    /* ==============================
       HANDLE KEY PRESS
    ============================== */
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (step === 2) {
                verifyOtp();
            } else {
                createAccount();
            }
        }
    };

    // Get current OTP array safely
    const currentOtp = form.otp || ["", "", "", "", "", ""];

    return (
        <div className="premium-verification-page">

            {/* ==============================
         TOP RIGHT HOME BUTTON
      ============================== */}
            <div className="premium-verification-home-container">
                <Link to="/" className="premium-verification-home-btn">
                    <FiHome size={20} />
                    <span>Home</span>
                </Link>
            </div>

            {/* ==============================
         MAIN CONTENT
      ============================== */}
            <div className="premium-verification-container">
                <div className="premium-verification-form-container">
                    <div className="premium-verification-form-card">

                        {/* Logo */}
                        <div className="premium-verification-form-logo">
                            {logoUrl ? (
                                <img src={logoUrl} alt="Brand Logo" className="premium-verification-form-brand-logo" />
                            ) : (
                                <div className="premium-verification-form-brand-text">Justivon</div>
                            )}
                        </div>

                        {/* STEP 2: OTP Verification */}
                        {step === 2 && (
                            <>
                                <div className="premium-verification-form-header">
                                    <h2 className="premium-verification-form-title">Verify Your Email</h2>
                                    <p className="premium-verification-form-subtitle">
                                        Enter the 6-digit OTP sent to your email address
                                    </p>
                                </div>

                                {/* Email Info */}
                                <div className="email-info">
                                    <p>OTP sent to:</p>
                                    <div className="email-address">{form.email}</div>
                                </div>

                                {/* OTP Timer */}
                                <div className="otp-timer">
                                    <FiClock size={16} style={{ marginRight: '8px', marginBottom: "5px", verticalAlign: 'middle' }} />
                                    OTP expires in <strong>{formatTime(otpTimer)}</strong>
                                </div>

                                {/* Message Display */}
                                {message && (
                                    <div className={`premium-verification-message ${messageType}`}>
                                        {messageType === "error" ? <FiAlertCircle /> : <FiCheckCircle />}
                                        <span>{message}</span>
                                    </div>
                                )}

                                {/* OTP Input Boxes */}
                                <div className="otp-containers">
                                    {[0, 1, 2, 3, 4, 5].map((index) => (
                                        <input
                                            key={index}
                                            ref={(el) => (otpRefs.current[index] = el)}
                                            className={`otp-input ${currentOtp[index] ? 'filled' : ''}`}
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            maxLength="1"
                                            value={currentOtp[index]}
                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                            onKeyPress={handleKeyPress}
                                            autoFocus={index === 0}
                                        />
                                    ))}
                                </div>

                                {/* Verify Button */}
                                <button
                                    className="premium-verification-submit-btn"
                                    onClick={verifyOtp}
                                    disabled={loading || getOtpString().length !== 6}
                                >
                                    {loading ? (
                                        <div className="premium-verification-spinner"></div>
                                    ) : (
                                        <FiCheckCircle size={18} />
                                    )}
                                    <span>{loading ? "Verifying..." : "Verify OTP"}</span>
                                </button>

                                {/* Resend OTP Button */}
                                <button
                                    className="premium-verification-secondary-btn"
                                    onClick={resendOtp}
                                    disabled={!canResend || loading}
                                >
                                    <FiMail size={18} />
                                    <span>{canResend ? "Resend OTP" : "Resend OTP"}</span>
                                </button>

                                {/* Back to Signup */}
                                <div className="back-to-login">
                                    <span>Need to edit details?</span>
                                    <Link to="/signup" className="back-to-login-link">
                                        Back to Signup
                                    </Link>
                                </div>
                            </>
                        )}

                        {/* STEP 3: Complete Profile */}
                        {step === 3 && (
                            <>
                                <div className="premium-verification-form-header">
                                    <h2 className="premium-verification-form-title">Complete Your Profile</h2>
                                    <p className="premium-verification-form-subtitle">
                                        Almost there! Just a few more details to set up your account
                                    </p>
                                </div>

                                {/* Message Display */}
                                {message && (
                                    <div className={`premium-verification-message ${messageType}`}>
                                        {messageType === "error" ? <FiAlertCircle /> : <FiCheckCircle />}
                                        <span>{message}</span>
                                    </div>
                                )}

                                {/* Role Selection */}
                                <div className="premium-verification-form-group">
                                    <label className="premium-verification-label">
                                        <FiUser size={16} />
                                        <span>Select Your Role *</span>
                                    </label>
                                    <select
                                        className="premium-verification-input"
                                        value={form.role}
                                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                                        onKeyPress={handleKeyPress}
                                    >
                                        <option value="">Choose your role...</option>
                                        <option value="USER">User (Looking for Legal Services)</option>
                                        <option value="SOLICITOR">Solicitor (Provide Legal Services)</option>
                                    </select>
                                </div>

                                {/* Solicitor Specific Fields */}
                                {form.role === "SOLICITOR" && (
                                    <>
                                        <div className="premium-verification-form-group">
                                            <label className="premium-verification-label">
                                                <FiBriefcase size={16} />
                                                <span>SRA Number *</span>
                                            </label>
                                            <div className="premium-verification-input-group">
                                                <input
                                                    className="premium-verification-input"
                                                    type="text"
                                                    placeholder="Enter your SRA registration number"
                                                    value={form.sraNumber || ""}
                                                    onChange={(e) => setForm({ ...form, sraNumber: e.target.value })}
                                                    onKeyPress={handleKeyPress}
                                                />
                                            </div>
                                        </div>

                                        <div className="premium-verification-form-group">
                                            <label className="premium-verification-label">
                                                <FiMapPin size={16} />
                                                <span>City</span>
                                            </label>
                                            <div className="premium-verification-input-group">
                                                <input
                                                    className="premium-verification-input"
                                                    type="text"
                                                    placeholder="Enter your city"
                                                    value={form.city || ""}
                                                    onChange={(e) => fetchCities(e.target.value)}
                                                    onKeyPress={handleKeyPress}
                                                />
                                                {citySuggestions.length > 0 && (
                                                    <ul className="city-suggestions-list">
                                                        {citySuggestions.map((c) => (
                                                            <li
                                                                key={c.id}
                                                                className="city-suggestion-item"
                                                                onClick={() => {
                                                                    setForm(prev => ({ ...prev, city: c.city }));
                                                                    setCitySuggestions([]);
                                                                }}
                                                            >
                                                                {c.label}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}

                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Create Account Button */}
                                <button
                                    className="premium-verification-submit-btn"
                                    onClick={createAccount}
                                    disabled={
                                        loading ||
                                        !form.role ||
                                        (form.role === "SOLICITOR" && !form.sraNumber)
                                    }
                                >
                                    {loading ? (
                                        <div className="premium-verification-spinner"></div>
                                    ) : (
                                        <FiCheckCircle size={18} />
                                    )}

                                    <span>
                                        {loading
                                            ? form.role === "SOLICITOR"
                                                ? "Verifying SRA..."
                                                : "Creating Account..."
                                            : form.role === "SOLICITOR"
                                                ? "Verify SRA"
                                                : "Complete Signup"}
                                    </span>
                                </button>

                                {/* Back to OTP Verification */}
                                <button
                                    className="premium-verification-secondary-btn"
                                    onClick={() => setStep(2)}
                                    disabled={loading}
                                >
                                    <FiArrowLeft size={18} />
                                    <span>Back to OTP Verification</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}